<template>
  <div v-if="isOpen" class="app-modal-tray">
    <div ref="draggableRef" class="app-modal-tray__wrapper">
      <ModalTrayItem
        v-for="([key, modal], index) in modalsList.entries()"
        :key="key"
        :modal
        :modal-key="key"
        :is-front-modal="key === frontModalKey"
        :class="
          hiddenItemsCount &&
            index >= itemsLength - hiddenItemsCount - 1
            ? key === frontModalKey
              ? 'order-1'
              : 'order-2'
            : undefined
        "
      />
    </div>
    <UIDropdown
      v-if="isMoreButtonVisible"
      popper-class="app-modal-tray__more-popper"
      placement="top-end"
    >
      <template #default="{ isShown, toggle }">
        <UIButton
          variant="gray"
          fill="light"
          size="xxs"
          :label="`${hiddenItemsCount} more...`"
          class="app-modal-tray__more"
          :class="{'app-modal-tray__more--active': isShown}"
          @click="toggle"
        />
      </template>
      <template #popper="{ hide }">
        <div class="app-modal-tray__dropdown">
          <ModalTrayItem
            v-for="([key, modal]) in hiddenItems"
            :key="key"
            :modal
            :modal-key="key"
            @click="hide"
          />
        </div>
      </template>
    </UIDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useModalsStore } from '@/store/modals'

import { ModalFactoryInstances } from '../ModalFactory/utils/types'
import ModalTrayItem from './ModalTrayItem.vue'
import { useTemplateRef } from 'vue'
import { UIDropdown } from '@ui/dropdowns'
import { UIButton } from '@ui/buttons'
import { useDebounceFn, useResizeObserver } from '@vueuse/core'

const draggableRef = useTemplateRef('draggableRef')

const modalsStore = useModalsStore()

const hiddenItemsCount = ref(0)

const isOpen = computed(() => itemsLength.value > 0)
const modalsList = computed(() => modalsStore.getTray as ModalFactoryInstances)
const itemsLength = computed(() => modalsList.value.size)

const hiddenItems = computed(() => Array.from(modalsList.value.entries()).slice(itemsLength.value - hiddenItemsCount.value))

const isMoreButtonVisible = computed(
  () => !!hiddenItemsCount.value,
)

const frontModalKey = computed(() => modalsStore.getFrontModalKey)

const handleResize = useDebounceFn(entries => {
  const target = entries[0]?.target
  if (!target) return
  hiddenItemsCount.value = target.querySelectorAll(
    '.app-modal-tray-item--hidden',
  ).length
}, 300)

useResizeObserver(draggableRef, handleResize)
</script>

<style>
.app-modal-tray {
  @apply flex items-center gap-2 shrink-0;
  @apply bg-body-dark border-t border-common-border;
  @apply pr-28;
  @apply overflow-hidden;

  &__wrapper {
    @apply h-8;
    @apply px-4;
    @apply flex items-center gap-1;
    @apply overflow-hidden;
  }

  &__dropdown {
    @apply flex flex-col items-start gap-1;
    @apply p-1;
  }

  &__more {
    @apply w-16;
    @apply px-0;
    @apply whitespace-nowrap;

    &--active {
      @apply bg-gray-100 dark:bg-gray-800;
    }
  }

  &__more-popper {
    .v-popper__inner {
      @apply min-w-40;
      @apply bg-body-dark;
    }
  }
}
</style>
